import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CustomButton, CustomReactSelect } from '../../atoms';
import { MdOutlineSearch } from "react-icons/md";
import { toast } from 'react-toastify';
import { apiGqAdminClient } from '../../../api-client/api-gqadmin-client/axios';
import classes from './GileSearchForm.module.scss';
import { getQueryParamsString } from '../../../utils/utils';
import { PAYMENT_PAGE_BASE_URL } from '../../../../constants';
import { useQuery } from '../../../hooks';
import { ANALYTICS_EVENTS } from '../../../utils/constants';
import { sendAnalytics } from '../../../analytics';

const GileSearchForm = ({ pageData }) => {
    const { search_block: { gile_search } } = pageData;
    if (!gile_search?.fields) return null;

    // Query Params
    const query = useQuery();

    // Search Level
    const [searchLevel, setSearchLevel] = useState('gileayc');
    // Group
    const [groupList, setGroupList] = useState([]);
    const [groupValue, setGroupValue] = useState(null);
    // Institute
    const [instituteList, setInstituteList] = useState([]);
    const [instituteValue, setInstituteValue] = useState(null);
    // Location
    const [locationList, setLocationList] = useState([]);
    const [locationValue, setLocationValue] = useState(null);
    // EducationType
    const [educationTypeList, setEducationTypeList] = useState([]);
    const [educationTypeValue, setEducationTypeValue] = useState(null);
    // Academic Year
    const [academicYearList, setAcademicYearList] = useState([]);
    const [academicYearValue, setAcademicYearValue] = useState(null);
    // Gilec 
    const [gilecList, setGilecList] = useState([]);
    const [gilecValue, setGilecValue] = useState(null);
    // Payment Pages List
    const [paymentPageList, setPaymentPageList] = useState([]);
    const [isPaymentPageListLoading, setIsPaymentPageListLoading] = useState(false);
    const [paymentPageValue, setPaymentPageValue] = useState(null);
    const [isPaymentPageDropdownDisabled, setIsPaymentPageDropdownDisabled] = useState(true);
    // URL 
    const [paymentPageUrl, setPaymentPageUrl] = useState("");
    const [allPramas, setAllPramas] = useState({});

    const validationSchema = Yup.object({
        group_code: Yup.string().required('Please enter group name'),
        institute_code: Yup.string().required('Please select institute name'),
        location_code: Yup.string().required('Please select location'),
        education_type_code: Yup.string().required('Please select education type'),
        // ay_code: Yup.string().required('Please select academic year'),
        ay_code: Yup.string().when(searchLevel, {
            is: 'gileay' || 'gileayc',
            then: Yup.string().required('Please select academic year'),
        }),
        // gilec_code: Yup.string().required('Please select class'),
        gilec_code: Yup.string().when(searchLevel, {
            is: 'gileayc',
            then: Yup.string().required('Please select class'),
        }),
        payment_page_slug: Yup.string().required('Please select payment page'),
	});

    const formik = useFormik({
		initialValues: {
            group_code: pageData?.institute_config?.group_code || '',
            institute_code: pageData?.institute_config?.institute_code || '',
            location_code: pageData?.institute_config?.location_code || '',
            education_type_code: pageData?.institute_config?.education_type_code || '',
            ay_code: pageData?.institute_config?.ay_code || '',
            gilec_code: pageData?.institute_config?.gilec_code || '',
            payment_page_slug: pageData?.institute_config?.payment_page_slug || '',
		},
		validationSchema,
        enableReinitialize: true,
		onSubmit: () => {
            formik.setSubmitting(true);
            const STATIC_DISTINCT_ID = document.documentElement.getAttribute("data-mxp-id");
            let forwardQueryParams = getQueryParamsString(allPramas);
            let url = new URL(paymentPageUrl);
            if (forwardQueryParams) {
                url.search = forwardQueryParams;
            }
            url.searchParams.set("mxp_did", STATIC_DISTINCT_ID);
            sendAnalytics({
                vendor: ['mixpanel', 'ga'],
                eventData: {
                    event: ANALYTICS_EVENTS.PP_SEARCH_PAGE_REDIRECT,
                    trackingData: {
                        redirect_slug: paymentPageUrl?.split['/'],
                    }
                }
            })
            if (gile_search?.cta_config?.new_window) {
                window.open(url.toString(), '_blank').focus();
            } else {
                window.open(url.toString(), '_self').focus();
            }
            formik.setSubmitting(false);
        },
	});

    // 1.1 Filter Query Type
    const filterQueryPayload = (queryPayload, dd_type) => {
        return {
            ...({ group_code: queryPayload?.group_code }),
            ...(['l', 'et', 'ay', 'gilec'].includes(dd_type) && { institute_code: queryPayload?.institute_code }),
            ...(['et', 'ay', 'gilec'].includes(dd_type) && { location_code: queryPayload?.location_code }),
            ...(['ay', 'gilec'].includes(dd_type) && { education_type_code: queryPayload?.education_type_code }),
            ...(['gilec'].includes(dd_type) && { ay_code: queryPayload?.ay_code }),
            is_order_by_id: true,
        }
    }

    // 1. Fetch GILE|AY|C Dropdown
    const fetchGileaycDependentDropdown = async (queryPayload, dd_type) => {
        if (!dd_type || !['i', 'l', 'et', 'ay', 'gilec'].includes(dd_type)) return;
        const queryString = getQueryParamsString(filterQueryPayload(queryPayload, dd_type));

        apiGqAdminClient({
            method: "GET",
            url: `/v1/payment-pages/gileayc/fetch?dropdown=true&${queryString}`,
        }).then((res) => {
            const dataList = res?.data?.data || [];
            if (dataList) {
                if (dd_type === 'g') {
                    setGroupList(dataList || []);
                    return;
                }
                if (dd_type === 'i') {
                    setInstituteList(dataList || []);
                    return;
                }
                if (dd_type === 'l') {
                    setLocationList(dataList || []);
                    return;
                }
                if (dd_type === 'et') {
                    setEducationTypeList(dataList || []);
                    return;
                }
                if (dd_type === 'ay') {
                    setAcademicYearList(dataList || []);
                    return;
                }
                if (dd_type === 'gilec') {
                    setGilecList(dataList || []);
                    return;
                }
            } 
            // else {
            //     toast.error("Oops! Something went wrong. Please contact support@grayquest.com");
            // }
        }).catch((err) => {
            console.error("fetchGileaycDependentDropdown error", err?.response?.data);
            toast.error(err?.response?.data?.message || "Oops! Something went wrong");
        });
    }

    // 2. Fetch Payment Pages List
    const fetchPaymentPagesList = async (payload) => {
        setIsPaymentPageListLoading(true);
        const queryString = getQueryParamsString(payload);
        apiGqAdminClient({
            method: "GET",
            url: `/v1/payment-pages/fetch?slug_dropdown=true&${queryString}`,
        }).then((res) => {
            if (res?.data?.data?.length > 0) {
                setPaymentPageList(res?.data?.data);
                setIsPaymentPageDropdownDisabled(false);
                if (res?.data?.data?.length === 1) {
                    formik?.setFieldValue('payment_page_slug', res?.data?.data?.[0]?.value);
                    setPaymentPageValue(res?.data?.data?.[0]);
                    setPaymentPageUrl(`${PAYMENT_PAGE_BASE_URL}/${res?.data?.data?.[0]?.value}`);
                    // TODO: Auto-Redirect if only one payment page is available
                    // if (gile_search?.cta_config?.new_window) {
                    //     window.open(paymentPageUrl, '_blank').focus();
                    // } else {
                    //     window.open(paymentPageUrl, '_self').focus();
                    // }
                }
            } else {
                toast.error("Oops! Something went wrong");
            }
        }).catch((err) => {
            console.error("fetchPaymentPagesList error", err);
            toast.error(err?.response?.data?.message || "Oops! Something went wrong");
        }).finally(() => setIsPaymentPageListLoading(false));
    }

    const onClear = async (e, context) => {
        try {
            const { action, name } = context || {};
            if (action === 'clear') {
                // reset all common data
                setPaymentPageUrl('');
                setPaymentPageValue(null);
                await formik.setFieldValue('payment_page_slug', '');

                switch (context.name) { 
                    case 'group_code':
                        setGroupValue(null);
                        await formik.setFieldValue('group_code', '');
                        await formik.setFieldValue('institute_code', '');
                        setInstituteValue(null);
                        await formik.setFieldValue('location_code', '');
                        setLocationValue(null);
                        await formik.setFieldValue('education_type_code', '');
                        setEducationTypeValue(null);
                        await formik.setFieldValue('ay_code', '');
                        setAcademicYearValue(null);
                        await formik.setFieldValue('gilec_code', '');
                        setGilecValue(null);
                        setIsPaymentPageDropdownDisabled(true);
                        break;
                    case 'institute_code':
                        setInstituteValue(null);
                        await formik.setFieldValue('institute_code', '');
                        await formik.setFieldValue('location_code', '');
                        setLocationValue(null);
                        await formik.setFieldValue('education_type_code', '');
                        setEducationTypeValue(null);
                        await formik.setFieldValue('ay_code', '');
                        setAcademicYearValue(null);
                        await formik.setFieldValue('gilec_code', '');
                        setGilecValue(null);
                        setIsPaymentPageDropdownDisabled(true);
                        break;
                    case 'location_code':
                        setLocationValue(null);
                        await formik.setFieldValue('location_code', '');
                        await formik.setFieldValue('education_type_code', '');
                        setEducationTypeValue(null);
                        await formik.setFieldValue('ay_code', '');
                        setAcademicYearValue(null);
                        await formik.setFieldValue('gilec_code', '');
                        setGilecValue(null);
                        setIsPaymentPageDropdownDisabled(true);
                        break;
                    case 'education_type_code':
                        setEducationTypeValue(null);
                        await formik.setFieldValue('education_type_code', '');
                        await formik.setFieldValue('ay_code', '');
                        setAcademicYearValue(null);
                        await formik.setFieldValue('gilec_code', '');
                        setGilecValue(null);
                        setIsPaymentPageDropdownDisabled(true);
                        break;
                    case 'ay_code':
                        setAcademicYearValue(null);
                        await formik.setFieldValue('ay_code', '');
                        await formik.setFieldValue('gilec_code', '');
                        setGilecValue(null);
                        setIsPaymentPageDropdownDisabled(true);
                        break;
                    case 'gilec_code':
                        setGilecValue(null);
                        await formik.setFieldValue('gilec_code', '');
                        setIsPaymentPageDropdownDisabled(true);
                        break;
                    case 'payment_page_slug':
                        break;
                    default:
                        break;
                }
            }
        } catch (err) {
            console.error('handleReactSelectChange - Clear - error', err)
        }
    }

    const handleReactSelectChange = async (e, context) => {
        try {
            if (context.action === 'clear') {
                onClear(e, context);
            } else {
                switch (context.name) { 
                    case 'group_code':
                        setGroupValue(e);
                        await formik.setFieldValue('group_code', e.value);
                        await formik.setFieldValue('institute_code', '');
                        setInstituteValue(null);
                        await fetchGileaycDependentDropdown({ 
                            group_code: e.value 
                        }, 'i');
                        await formik.setFieldValue('location_code', '');
                        setLocationValue(null);
                        await formik.setFieldValue('education_type_code', '');
                        setEducationTypeValue(null);
                        break;
                    case 'institute_code':
                        setInstituteValue(e);
                        await formik.setFieldValue('institute_code', e.value);
                        await formik.setFieldValue('location_code', '');
                        setLocationValue(null);
                        await fetchGileaycDependentDropdown({ 
                            group_code: groupValue?.value, 
                            institute_code: e.value 
                        }, 'l');
                        await formik.setFieldValue('education_type_code', '');
                        setEducationTypeValue(null);
                        break;
                    case 'location_code':
                        setLocationValue(e);
                        await formik.setFieldValue('location_code', e.value);
                        await formik.setFieldValue('education_type_code', '');
                        setEducationTypeValue(null);
                        await fetchGileaycDependentDropdown({ 
                            group_code: groupValue?.value, 
                            institute_code: instituteValue?.value, 
                            location_code: e.value 
                        }, 'et');
                        break;
                    case 'education_type_code':
                        await formik.setFieldValue('education_type_code', e.value);
                        setEducationTypeValue(e);
                        // Payment Page Search Level - gile
                        if (searchLevel === 'gile') {
                            await fetchPaymentPagesList({
                                group_code: groupValue?.value,
                                institute_code: instituteValue?.value,
                                location_code: locationValue?.value,
                                education_type_code: e.value,
                            });
                            return;
                        }
                        await fetchGileaycDependentDropdown({
                            group_code: groupValue?.value,
                            institute_code: instituteValue?.value,
                            location_code: locationValue?.value,
                            education_type_code: e.value,
                        }, 'ay');
                        break;
                    case 'ay_code':
                        await formik.setFieldValue('ay_code', e.value);
                        setAcademicYearValue(e);
                        // Payment Page Search Level - gileay
                        if (searchLevel === 'gileay') {
                            await fetchPaymentPagesList({
                                group_code: groupValue?.value,
                                institute_code: instituteValue?.value,
                                location_code: locationValue?.value,
                                education_type_code: educationTypeValue?.value,
                                ay_code: e.value,
                            });
                            return;
                        }
                        await fetchGileaycDependentDropdown({
                            group_code: groupValue?.value,
                            institute_code: instituteValue?.value,
                            location_code: locationValue?.value,
                            education_type_code: educationTypeValue?.value,
                            ay_code: e.value,
                        }, 'gilec');
                        break;
                    case 'gilec_code':
                        await formik.setFieldValue('gilec_code', e.value);
                        await formik.setFieldValue('payment_page_slug', '');
                        setGilecValue(e);
                        setPaymentPageValue(null);
                        setPaymentPageUrl('');
                        // Payment Page Search Level - gileay
                        await fetchPaymentPagesList({
                            group_code: groupValue?.value,
                            institute_code: instituteValue?.value,
                            location_code: locationValue?.value,
                            education_type_code: educationTypeValue?.value,
                            ay_code: academicYearValue?.value,
                            gilec_code: e.value,
                        });
                        break;
                    case 'payment_page_slug':
                        await formik.setFieldValue('payment_page_slug', e.value);
                        setPaymentPageValue(e);
                        setPaymentPageUrl(`${PAYMENT_PAGE_BASE_URL}/${e?.value}`);
                        break;
                    default:
                        break;
                }
            }
        } catch (error) {
            console.error("handleReactSelectChange - select - error", error);
        }
    };
    

    // const handleGroupInputChange = (e) => {
    //     const inputValue = e.trim();
    //     ("group input value", inputValue);
    // }

    useEffect( async () => {
        setAllPramas(Object.fromEntries(query.entries()));

        if (gile_search?.search_level && ['gile', 'gileay'].includes(gile_search?.search_level)) {
            setSearchLevel(gile_search?.search_level || 'gileayc');
        }
        if (pageData?.institute_config?.group_code && pageData?.institute_config?.group_label) {
            setGroupValue({
                label: pageData.institute_config.group_label,
                value: pageData.institute_config.group_code
            });
            if (pageData?.institute_config?.institute_code && pageData?.institute_config?.institute_label) {
                setInstituteValue({
                    label: pageData.institute_config.institute_label,
                    value: pageData.institute_config.institute_code
                });
                if (pageData?.institute_config?.location_code && pageData?.institute_config?.location_label) {
                    setLocationValue({
                        label: pageData.institute_config.location_label,
                        value: pageData.institute_config.location_code
                    });
                    if (pageData?.institute_config?.education_type_code && pageData?.institute_config?.education_type_label) {
                        setEducationTypeValue({
                            label: pageData.institute_config.education_type_label,
                            value: pageData.institute_config.education_type_code
                        });
                        if (pageData?.institute_config?.ay_code && pageData?.institute_config?.ay_label) {
                            setAcademicYearValue({
                                label: pageData.institute_config.ay_label,
                                value: pageData.institute_config.ay_code
                            });
                            if (pageData?.institute_config?.gilec_code && pageData?.institute_config?.gilec_label) {
                                setGilecValue({
                                    label: pageData.institute_config.gilec_label,
                                    value: pageData.institute_config.gilec_code
                                });
                                if (pageData?.institute_config?.payment_page_slug && pageData?.institute_config?.payment_page_label) {
                                    setPaymentPageValue({
                                        label: pageData.institute_config.payment_page_label,
                                        value: pageData.institute_config.payment_page_slug
                                    });
                                } else {
                                    // Fetch Payment Pages
                                    await fetchPaymentPagesList({ 
                                        group_code: pageData.institute_config.group_code, 
                                        institute_code: pageData.institute_config.institute_code, 
                                        location_code: pageData.institute_config.location_code, 
                                        education_type_code: pageData.institute_config.education_type_code, 
                                        ay_code: pageData.institute_config.ay_code, 
                                        gilec_code: pageData.institute_config.gilec_code, 
                                    });
                                }
                            } else {
                                if (gile_search?.search_level === 'gileay') {
                                    // Fetch Payment Pages
                                    await fetchPaymentPagesList({ 
                                        group_code: pageData.institute_config.group_code, 
                                        institute_code: pageData.institute_config.institute_code, 
                                        location_code: pageData.institute_config.location_code, 
                                        education_type_code: pageData.institute_config.education_type_code, 
                                        ay_code: pageData.institute_config.ay_code, 
                                    });
                                } else {
                                    await fetchGileaycDependentDropdown({ 
                                        group_code: pageData.institute_config.group_code, 
                                        institute_code: pageData.institute_config.institute_code, 
                                        location_code: pageData.institute_config.location_code, 
                                        education_type_code: pageData.institute_config.education_type_code, 
                                        ay_code: pageData.institute_config.ay_code, 
                                    }, 'gilec');
                                }
                            }
                        } else {
                            if (gile_search?.search_level === 'gile') {
                                // Fetch Payment Pages
                                await fetchPaymentPagesList({ 
                                    group_code: pageData.institute_config.group_code, 
                                    institute_code: pageData.institute_config.institute_code, 
                                    location_code: pageData.institute_config.location_code, 
                                    education_type_code: pageData.institute_config.education_type_code, 
                                });
                            } else {
                                await fetchGileaycDependentDropdown({ 
                                    group_code: pageData.institute_config.group_code, 
                                    institute_code: pageData.institute_config.institute_code, 
                                    location_code: pageData.institute_config.location_code, 
                                    education_type_code: pageData.institute_config.education_type_code, 
                                }, 'ay');
                            }
                        }
                    } else {
                        await fetchGileaycDependentDropdown({ 
                            group_code: pageData.institute_config.group_code, 
                            institute_code: pageData.institute_config.institute_code, 
                            location_code: pageData.institute_config.location_code, 
                        }, 'et');
                    }
                } else {
                    fetchGileaycDependentDropdown({ 
                        group_code: pageData.institute_config.group_code, 
                        institute_code: pageData.institute_config.institute_code, 
                    }, 'l');
                }
            } else {
                fetchGileaycDependentDropdown({ 
                    group_code: pageData.institute_config.group_code 
                }, 'i');
            }
        }
    }, [])
    
    return (
        <form className={classes.GileSearchForm} onSubmit={formik.handleSubmit}>
            <div className="row">
                <div className="col-lg-6 mx-auto">
                    
                    {(gile_search?.fields?.group_field?.visible && !pageData?.institute_config?.group_code) ? (
                        <div className={classes.FormFieldCol}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label htmlFor="group_code">{gile_search?.fields?.group_field?.label || 'Group'} <span className="required">*</span></label>
                                    <CustomReactSelect
                                        inputId="group_code"
                                        name="group_code"
                                        icon={<MdOutlineSearch />}
                                        maxMenuHeight={200}
                                        onChange={handleReactSelectChange}
                                        // onInputChange={handleGroupInputChange}
                                        // onKeyDown={handleInstituteInputChange}
                                        options={groupList || []}
                                        value={groupValue || null}
                                        placeholder={gile_search?.fields?.group_field?.placeholder || "Search for group"}
                                        noOptionsText={groupList.length === 0 ? "Search for group (enter 3 or more characters)" : "No options"}
                                        showDisabledIcon
                                        isDisabled={false}
                                        isLoading={false}
                                        isClearable
                                    />
                                    {formik.touched.group_code && formik.errors.group_code ? (
                                        <span className="form-error">
                                            {gile_search?.fields?.group_field?.label ? `Please enter ${gile_search.fields.group_field.label}` : <>{formik.errors.group_code}</>}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {(gile_search?.fields?.institute_field?.visible && !pageData?.institute_config?.institute_code) ? (
                        <div className={classes.FormFieldCol}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label htmlFor="institute_code">{gile_search?.fields?.institute_field?.label || "Institute"} <span className="required">*</span></label>
                                    <CustomReactSelect
                                        inputId="institute_code"
                                        name="institute_code"
                                        icon={<MdOutlineSearch />}
                                        maxMenuHeight={200}
                                        onChange={handleReactSelectChange}
                                        options={instituteList || []}
                                        value={instituteValue || null}
                                        placeholder={gile_search?.fields?.institute_field?.placeholder || "Search for institute"}
                                        showDisabledIcon
                                        isDisabled={!groupValue?.value}
                                        isLoading={false}
                                        isClearable
                                    />
                                    {formik.touched.institute_code && formik.errors.institute_code ? (
                                        <span className="form-error">
                                            {gile_search?.fields?.institute_field?.label ? `Please select ${gile_search.fields.institute_field.label}` : <>{formik.errors.institute_code}</>}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {(gile_search?.fields?.location_field?.visible && !pageData?.institute_config?.location_code) ? (
                        <div className={classes.FormFieldCol}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label htmlFor="location_code">{gile_search?.fields?.location_field?.label || "Location"} <span className="required">*</span></label>
                                    <CustomReactSelect
                                        inputId="location_code"
                                        name="location_code"
                                        icon={<MdOutlineSearch />}
                                        maxMenuHeight={200}
                                        onChange={handleReactSelectChange}
                                        options={locationList || []}
                                        value={locationValue || null}
                                        placeholder={gile_search?.fields?.location_field?.placeholder || "Search for location"}
                                        showDisabledIcon
                                        isDisabled={!instituteValue?.value}
                                        isLoading={false}
                                        isClearable
                                    />
                                    {formik.touched.location_code && formik.errors.location_code ? (
                                        <span className="form-error">
                                            {gile_search?.fields?.location_field?.label ? `Please select ${gile_search.fields.location_field.label}` : <>{formik.errors.location_code}</>}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {(gile_search?.fields?.education_type_field?.visible && !pageData?.institute_config?.education_type_code) ? (
                        <div className={classes.FormFieldCol}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label htmlFor="education_type_code">{gile_search?.fields?.education_type_field?.label || "Education Type"} <span className="required">*</span></label>
                                    <CustomReactSelect
                                        inputId="education_type_code"
                                        name="education_type_code"
                                        icon={<MdOutlineSearch />}
                                        maxMenuHeight={200}
                                        onChange={handleReactSelectChange}
                                        options={educationTypeList || []}
                                        value={educationTypeValue || null}
                                        placeholder={gile_search?.fields?.education_type_field?.placeholder || "Search for education type"}
                                        showDisabledIcon
                                        isDisabled={!locationValue?.value}
                                        isLoading={false}
                                        isClearable
                                    />
                                    {formik.touched.education_type_code && formik.errors.education_type_code ? (
                                        <span className="form-error">
                                            {gile_search?.fields?.education_type_field?.label ? `Please select ${gile_search.fields.education_type_field.label}` : <>{formik.errors.education_type_code}</>}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}
                    
                    {(gile_search?.fields?.academic_year_field?.visible && !pageData?.institute_config?.ay_code && ['gileay', 'gileayc'].includes(searchLevel)) ? (
                        <div className={classes.FormFieldCol}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label htmlFor="ay_code">{gile_search?.fields?.academic_year_field?.label || "Academic Year"} <span className="required">*</span></label>
                                    <CustomReactSelect
                                        inputId="ay_code"
                                        name="ay_code"
                                        icon={<MdOutlineSearch />}
                                        maxMenuHeight={200}
                                        onChange={handleReactSelectChange}
                                        options={academicYearList || []}
                                        value={academicYearValue || null}
                                        placeholder={gile_search?.fields?.academic_year_field?.placeholder || "Search for academic year"}
                                        showDisabledIcon
                                        isDisabled={!educationTypeValue?.value}
                                        isLoading={false}
                                        isClearable
                                    />
                                    {formik.touched.ay_code && formik.errors.ay_code ? (
                                        <span className="form-error">
                                            {gile_search?.fields?.academic_year_field?.label ? `Please select ${gile_search.fields.academic_year_field.label}` : <>{formik.errors.ay_code}</>}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {(gile_search?.fields?.class_field?.visible && !pageData?.institute_config?.gilec_code && ['gileayc'].includes(searchLevel)) ? (
                        <div className={classes.FormFieldCol}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <label htmlFor="gilec_code">{gile_search?.fields?.class_field?.label || "Class"} <span className="required">*</span></label>
                                    <CustomReactSelect
                                        inputId="gilec_code"
                                        name="gilec_code"
                                        icon={<MdOutlineSearch />}
                                        maxMenuHeight={200}
                                        onChange={handleReactSelectChange}
                                        options={gilecList || []}
                                        value={gilecValue || null}
                                        placeholder={gile_search?.fields?.class_field?.placeholder || "Search for class"}
                                        showDisabledIcon
                                        isDisabled={!academicYearValue?.value}
                                        isLoading={false}
                                        isClearable
                                    />
                                    {formik.touched.gilec_code && formik.errors.gilec_code ? (
                                        <span className="form-error">
                                            {gile_search?.fields?.class_field?.label ? `Please select ${gile_search.fields.class_field.label}` : <>{formik.errors.gilec_code}</>}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className={classes.FormFieldCol}>
                        <div className="row">
                            <div className="col-lg-12">
                                <label htmlFor="payment_page_slug">{gile_search?.fields?.payment_page_field?.label || "Payment Type"} <span className="required">*</span></label>
                                <CustomReactSelect
                                    inputId="payment_page_slug"
                                    name="payment_page_slug"
                                    icon={<MdOutlineSearch />}
                                    maxMenuHeight={200}
                                    onChange={handleReactSelectChange}
                                    options={paymentPageList || []}
                                    value={paymentPageValue || null}
                                    placeholder={gile_search?.fields?.payment_page_field?.placeholder || "Search for payment type"}
                                    showDisabledIcon
                                    isDisabled={isPaymentPageDropdownDisabled}
                                    isLoading={isPaymentPageListLoading}
                                    isClearable
                                />
                                {formik.touched.payment_page_slug && formik.errors.payment_page_slug ? (
                                    <span className="form-error">
                                        {gile_search?.fields?.payment_page_field?.label ? `Please select ${gile_search.fields.payment_page_field.label}` : <>{formik.errors.payment_page_slug}</>}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {(gile_search?.fields?.group_field?.visible || 
                        gile_search?.fields?.institute_field?.visible ||
                        gile_search?.fields?.location_field?.visible || 
                        gile_search?.fields?.education_type_field?.visible ||
                        gile_search?.fields?.academic_year_field?.visible || 
                        gile_search?.fields?.gilec_field?.visible || 
                        gile_search?.fields?.payment_page_field?.visible) ? (
                            <div className={classes.FormFieldCol}>
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className={classes.FormAction}>
                                            <CustomButton
                                                type="submit"
                                                text={gile_search?.cta_config?.label || "Proceed"}
                                                // variant={(!formik.isValid && !formik.dirty) ? 'outline' : ''}
                                                className={classes.SubmitButton}
                                                isLoading={formik.isSubmitting}
                                                isDisabled={!paymentPageUrl}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                </div>
            </div>
        </form>
    )
}

GileSearchForm.propTypes = {}

export default GileSearchForm;